import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { Customer } from '../../../shared/types/Customer';
import { getTenant } from '../../../theme/tokens';
import { makeCompanyName, makeEmail, makePhone, profileCity, profileStreet, profileCountry } from '../../../shared/config/localeProfile';
export function Customers() {
  const {
    t
  } = useTranslation('customers');
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const {
    code: currencyCode
  } = getTenant().currency;
  const locale = getTenant().locale.default;
  const formatAmount = (amount: number) => `${currencyCode} ${amount.toLocaleString(locale)}`;
  const customers: Customer[] = [{
    id: '1',
    name: makeCompanyName('Acme Trading'),
    email: makeEmail('billing', 'acme'),
    phone: makePhone('33 4567'),
    address: profileStreet(0),
    city: profileCity(0),
    country: profileCountry(),
    createdAt: '2023-06-15',
    totalSpent: 875000,
    invoiceCount: 12,
    status: 'active'
  }, {
    id: '2',
    name: makeCompanyName('Tech Solutions'),
    email: makeEmail('accounts', 'techsolutions'),
    phone: makePhone('32 1234'),
    address: profileStreet(1),
    city: profileCity(1),
    country: profileCountry(),
    createdAt: '2023-08-20',
    totalSpent: 450000,
    invoiceCount: 8,
    status: 'active'
  }, {
    id: '3',
    name: makeCompanyName('Atoll Enterprises'),
    email: makeEmail('finance', 'atollent'),
    phone: makePhone('68 9 7890'),
    address: profileStreet(2),
    city: profileCity(2),
    country: profileCountry(),
    createdAt: '2023-04-10',
    totalSpent: 1250000,
    invoiceCount: 15,
    status: 'active'
  }, {
    id: '4',
    name: makeCompanyName('Innovation Hub'),
    email: makeEmail('billing', 'innovationhub'),
    phone: makePhone('33 8901'),
    address: profileStreet(3),
    city: profileCity(3),
    country: profileCountry(),
    createdAt: '2023-09-05',
    totalSpent: 180000,
    invoiceCount: 4,
    status: 'inactive'
  }];
  const getStatusBadge = (status: Customer['status']) => {
    const styles = {
      active: 'bg-green-50 text-green-700 border border-green-100',
      inactive: 'bg-gray-100 text-gray-600 border border-gray-200'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${styles[status]}`} data-id="element-5497" data-mp="36cg3hz9c0e85">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) || customer.email.toLowerCase().includes(searchTerm.toLowerCase()) || customer.phone && customer.phone.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const totalCustomers = filteredCustomers.length;
  const activeCustomers = filteredCustomers.filter(c => c.status === 'active').length;
  const totalRevenue = filteredCustomers.reduce((sum, c) => sum + c.totalSpent, 0);
  const avgRevenue = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-5498" data-mp="1nbxb1kllzpib">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-5499" data-mp="1ne1fc3nf6qj4">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5500" data-mp="1idvw9bv7uccn">
          {t('pages.Customers.customers')}
        </h1>
        <button onClick={() => navigate('/customer-form')} className="px-4 py-2 bg-[var(--brand-primary)] hover:bg-[var(--brand-primaryhover)] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-5501" data-mp="3hpg6mow2wy0z">
          {t('pages.Customers.add_customer')}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-5502" data-mp="20f9ncdc7r8cc">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-5503" data-mp="20ezzowen5d9t">
          <div className="text-sm font-medium text-gray-500" data-id="element-5504" data-mp="25d1f97cuyugj">
            {t('pages.Customers.total_customers')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-5505" data-mp="1qndgacs5g7vz">
            {totalCustomers}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-5506" data-mp="1hp1ssjmkxq7r">
          <div className="text-sm font-medium text-gray-500" data-id="element-5507" data-mp="1lqd89wbqov28">
            {t('pages.Customers.active_customers')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-green-600" data-id="element-5508" data-mp="17qc7egvh5nmr">
            {activeCustomers}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-5509" data-mp="1cp5nmh25bsqm">
          <div className="text-sm font-medium text-gray-500" data-id="element-5510" data-mp="t4yh4udftaev">
            {t('pages.Customers.total_revenue')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-5511" data-mp="o6n3e8k0ufrd">
            {formatAmount(totalRevenue)}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-5512" data-mp="y39moswp8yra">
          <div className="text-sm font-medium text-gray-500" data-id="element-5513" data-mp="1c2rihs8exca5">
            {t('pages.Customers.avg_revenuecustomer')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-5514" data-mp="174g73d2gytpt">
            {formatAmount(Math.round(avgRevenue))}
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-5515" data-mp="1gt7ud7wl4pue">
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-5516" data-mp="1uu27u4yhpgzx">
          {/* Status Filter Tabs */}
          <div className="flex space-x-6" data-id="element-5517" data-mp="1xv3w7tvagyl4">
            <button onClick={() => setStatusFilter('all')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${statusFilter === 'all' ? 'border-[var(--brand-primary)] text-[var(--colors-primary-700)]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-5518" data-mp="1xw6q8anwrh59">
              {t('pages.Customers.all')}
            </button>
            <button onClick={() => setStatusFilter('active')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${statusFilter === 'active' ? 'border-[var(--brand-primary)] text-[var(--colors-primary-700)]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-5519" data-mp="2gkbnr0dz25vm">
              {t('pages.CustomerForm.active')}
            </button>
            <button onClick={() => setStatusFilter('inactive')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${statusFilter === 'inactive' ? 'border-[var(--brand-primary)] text-[var(--colors-primary-700)]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-5520" data-mp="w6jnu13stiem">
              {t('pages.CustomerForm.inactive')}
            </button>
          </div>

          {/* Search */}
          <div className="relative w-64" data-id="element-5521" data-mp="35k6kk3340h1b">
            <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-5522" data-mp="30o9662bepkr6" />

            <input type="search" aria-label={t('pages.Customers.attrs.search_by_customer')} placeholder={t('pages.Customers.attrs.search_by_customer')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--brand-primary)] transition-colors" data-id="element-5523" data-mp="1603dvw4jy1m" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-5524" data-mp="bu2ui75qyiqr">
          <table className="w-full text-left border-collapse" data-id="element-5525" data-mp="guqwtozfem0a">
            <thead data-id="element-5526" data-mp="gw1z1kd83lpa">
              <tr className="border-b border-gray-100" data-id="element-5527" data-mp="gse4qizyev9i">
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5528" data-mp="1yrqairystx9">
                  {t('pages.Customers.customer')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5529" data-mp="ullxidzrtwss">
                  {t('pages.Customers.contact')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5530" data-mp="zn2va0myl355">
                  {t('pages.Customers.location')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5531" data-mp="ptm28yunbs2j">
                  {t('pages.CustomerDetail.total_spent')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5532" data-mp="1ichx3xagxwal">
                  {t('pages.CustomerDetail.invoices')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-5533" data-mp="2y3twuhfoi84t">
                  {t('pages.CustomerDetail.status_2')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-5534" data-mp="37u6gm7fo5x9w">
              {filteredCustomers.map(customer => <tr key={customer.id} onClick={() => navigate(`/customer-detail/${customer.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-5535" data-mp="2k3j57c6jopil">
                  <td className="px-6 py-4" data-id="element-5536" data-mp="2p2lyjzd5x7aq">
                    <div className="text-sm font-medium text-gray-900 group-hover:text-[#9B1117] transition-colors" data-id="element-5537" data-mp="2p0spkdf2uazk">
                      {customer.name}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5" data-id="element-5538" data-mp="4b3co5xxoz9i">
                      {t('pages.Customers.since')}{' '}
                      {new Date(customer.createdAt).toLocaleDateString('en-GB')}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-5539" data-mp="22ezovfhmmc9o">
                    <div className="text-sm text-gray-900" data-id="element-5540" data-mp="1xkvgcrsppw6h">
                      <div className="flex items-center gap-1.5 mb-1" data-id="element-5541" data-mp="1smtvq2fn1osw">
                        <MailIcon size={13} className="text-gray-400" data-id="element-5542" data-mp="1smk7oke5lrpu" />
                        {customer.email}
                      </div>
                      {customer.phone && <div className="flex items-center gap-1.5" data-id="element-5543" data-mp="1in32s56q7hux">
                          <PhoneIcon size={13} className="text-gray-400" data-id="element-5544" data-mp="1em9evbtaa321" />
                          {customer.phone}
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-5545" data-mp="38zr556l2pe0r">
                    <div className="text-sm text-gray-900" data-id="element-5546" data-mp="38yppspx0y5d8">{customer.city}</div>
                    <div className="text-sm text-gray-500 mt-0.5" data-id="element-5547" data-mp="342r1117salca">
                      {customer.country}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-5548" data-mp="3gjtbxa6nepva">
                    <div className="text-sm font-medium text-gray-900" data-id="element-5549" data-mp="3gii9pesupq6a">
                      {formatAmount(customer.totalSpent)}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-5550" data-mp="3q9v2c8mh4kdd">
                    <div className="text-sm text-gray-900" data-id="element-5551" data-mp="2smmcy2jcqr63">
                      {customer.invoiceCount}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-5552" data-mp="2xm8xkx1if9ox">
                    {getStatusBadge(customer.status)}
                  </td>
                </tr>)}
              {filteredCustomers.length === 0 && <tr data-id="element-5553" data-mp="36kav32m1bk7g">
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-5554" data-mp="huuvb2ihkci5">
                    {t('pages.Customers.no_customers_found_matching_your_criteria')}
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}